import { useMemo } from 'react';
import _reduce from 'lodash/reduce';

import { ExternalEventFragment } from '@/modules/events/graphql/fragments/generated/ExternalEvent';
import useTimeConversions from '@/modules/events/hooks/useTimeConversions';
import {
  CertificateTemplateType,
  EventStatusEnum,
  EventTypeEnum,
  EventUnion,
  ExternalEventType,
  ResourceTypeEnum,
} from '@/types/schema';

import { EventFragment } from '../../../graphql/fragments/generated/Event';
import { OfflineEventFragment } from '../../../graphql/fragments/generated/OfflineEvent';
import { isSeatAvailable } from '../utils/getSeatsLeftInfo';

export const EVENT_RESOURCE_TYPE_MAP = {
  [EventTypeEnum.External]: ResourceTypeEnum.ExternalEventContent,
  [EventTypeEnum.Live]: ResourceTypeEnum.LiveEventContent,
  [EventTypeEnum.Offline]: ResourceTypeEnum.OfflineEventContent,
  [EventTypeEnum.RecurringExternal]: ResourceTypeEnum.ExternalEventContent,
  [EventTypeEnum.RecurringLive]: ResourceTypeEnum.LiveEventContent,
  [EventTypeEnum.RecurringOffline]: ResourceTypeEnum.OfflineEventContent,
};

const useEventResponse = (event: EventUnion) => {
  const { startTimeInMilliseconds, duration, eventTime, isRegistrationOpen } =
    useTimeConversions(event);

  return useMemo(
    () => ({
      id: event?.id,
      content: {
        name: event?.name,
        description: event?.description,
        contentType: 'event',
        picture: event?.cover?.url,
        certificateSettings: event?.certificateSettings,
        startTime: event?.startTime,
        endTime: event?.endTime,
        duration,
        startTimeInMilliseconds,
        objective: event?.objective,
        eventTime,
        instructors: event?.instructors,
        timezone: event?.timezone,
      },
      instructors: event?.instructors?.map((instructor, index) => ({
        id: index,
        avatarSrc: instructor.profilePicture,
        name: instructor.name,
        about: instructor.alias,
        picture: instructor.profilePicture,
        orgRole: instructor.orgRole?.name,
      })),
      totalEnrolled: null,
      type: event?.type,
      subType: event?.type === EventTypeEnum.Live ? (event as EventFragment)?.subType : undefined,
      skills: event?.skills?.map((skill) => ({ id: skill.name, name: skill.name })),
      // eslint-disable-next-line prettier/prettier
      status: event?.status,
      isRegistrationRequired: !!event?.registrationSettings,
      seatLimit: event?.registrationSettings?.seatLimit,
      registeredEventsCountInParent: event?.parentEvent?.registeredEventsCount ?? 0,
      multiSlotRegistration: Boolean(event?.registrationSettings?.multiSlotRegistration),
      usersCount: event?.usersCount,
      isUserRegistered: event?.isUserRegistered,
      subWorkspaces: event?.subWorkspaces,
      isRegistrationOpen,
      certificateTemplate: event?.certificateTemplate as CertificateTemplateType | undefined,
      issuedCertificate: event?.issuedCertificate,
      userPermissions: event?.userPermissions || [],
      isSeatsAvailable: isSeatAvailable(
        event?.availableSeatCount,
        event?.registrationSettings?.seatLimit!,
      ),
      isUserInstructor: event?.isUserInstructor,
      isFillingFast: event?.isFillingFast,
      location:
        event?.type === EventTypeEnum.Offline
          ? (event as OfflineEventFragment).location
          : undefined,
      isRecordingAvailable:
        event?.type === EventTypeEnum.Live
          ? (event as EventFragment)?.recordingSettings?.shareRecording
          : undefined,
      recordingSettings: (event as EventFragment)?.recordingSettings,
      eventProgress: (event as ExternalEventFragment)?.eventProgress,
      shareConfigs: event?.shareConfigs,
      sharedByWorkspace: event?.sharedByWorkspace,
      eventRecordings: event?.eventRecordings,
      tags: event?.tags || [],
      visibilitySettings: event?.visibilitySettings,
      availableInWorkspaces: event?.availableInWorkspaces,
      totalUsersCount: event?.totalUsersCount,
      availableSeatCount: event?.availableSeatCount,
      isFinishedSessionRevoked: event?.isRevokedAfterCompletion,
      latestUpcomingEventId: event?.latestUpcomingEventId,
      firstRegisteredSlotId: event?.parentEvent?.firstRegisteredSlotId,
      isUpcomingSessionRevoked:
        event?.status === EventStatusEnum.Revoked && !event?.isRevokedAfterCompletion,
      externalEventProviderInfo:
        event?.type === EventTypeEnum.External
          ? (event as ExternalEventType)?.externalEventProviderInfo
          : undefined,
      preWorkOptional: _reduce(
        event.prework?.contents,
        (result, it) => {
          result[it.contentId] = it.isOptional;
          return result;
        },
        {} as { [key: string]: boolean },
      ),
      postWorkOptional: _reduce(
        event.postwork?.contents,
        (result, it) => {
          result[it.contentId] = it.isOptional;
          return result;
        },
        {} as { [key: string]: boolean },
      ),
      remainingPreWorkContents: event?.remainingPreWorkContents,
      remainingPostWorkContents: event?.remainingPostWorkContents,
      event,
      resourceType: EVENT_RESOURCE_TYPE_MAP[event?.type!],
      completionSettings: event?.completionSettings,
      recurringCategory: event?.recurringCategory,
      parentEvent: event?.parentEvent,
    }),
    [event, duration, startTimeInMilliseconds, eventTime, isRegistrationOpen],
  );
};

export type UseEventResponseReturnType = ReturnType<typeof useEventResponse>;
export default useEventResponse;
